import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Routes, Route, useParams, Navigate } from 'react-router-dom';
import _ from 'lodash';
import { Box, CircularProgress } from '@mui/material';
import { datadogRum } from '@datadog/browser-rum';
import '@aws-amplify/ui-react/styles.css';
// import '../../../components/ContentEditorShared/styles.css'; // Only if this is the editor CSS, otherwise leave as is
import './styles.css';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { getEnv } from 'data_sources/constants';
import { useNavigate } from 'react-router-dom';

import ChangePassword from './ChangePassword';
import SwitchOrganization from './SwitchOrganization';
import Dashboard from './Dashboard';
import MFASettings from './MFASettings';
import Notifications from './Notifications';
import Error from './Error';
import AWB from './AWB';
import LexID from './LexID';
import Policy from './Policy';
import Reporting from './Reporting';
import Training from './Training';
import PerformanceReporting from './PerformanceReporting';
import LexAI from './LexAI/index';
import { AppContext } from '../context';
import AuthUtil from 'utils/AuthUtil';
import IssuedManualsSearchPage from './Policy/SearchManuals';
import DTBRoutes from './DTB';
import Features from './Features';
import KB from './KB';
import dayjs from 'dayjs';
import { User } from 'oidc-client-ts';
// @ts-ignore
import 'utils/scripts/setSampleLists';
import { secureStorage } from 'utils/secureStorage';
import Accreditation from './AWB/Accreditation';

/* main portal component */
const Portal: React.FC = () => {
  const { appPermissions, currentProfile, orgPermissions, isRoot, setUserOrganizationId } = useContext(AppContext);
  const navigate = useNavigate();

  const auth = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAttempt, setLastRefreshAttempt] = useState<number>(0);
  const REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refresh attempts

  const handleTokenRefresh = useCallback(async () => {
    const now = Date.now();
    if (isRefreshing || now - lastRefreshAttempt < REFRESH_COOLDOWN) {
      return;
    }

    setIsRefreshing(true);
    setLastRefreshAttempt(now);

    try {
      const user = await auth.signinSilent();
      if (user) {
        const oidcUser = user as User;
        secureStorage.setItem('lexat', oidcUser?.access_token || '');

        // Only set localStorage if not in signout process
        if (AuthUtil.shouldAllowStorageOperations()) {
          localStorage.setItem('phx-currentUserName', oidcUser?.profile['cognito:username']?.toString() || '');
          const orgId = oidcUser?.profile['custom:orgId']?.toString();
          if (orgId) {
            // Always set phx-currentOrganizationId to phx-userOrgId if userOrgId is not lexipoladminstaffuuid
            if (orgId !== 'lexipoladminstaffuuid') {
              localStorage.setItem('phx-currentOrganizationId', orgId);
            }
            setUserOrganizationId(orgId);
            localStorage.setItem('phx-userOrgId', orgId);
          }
        }
      } else {
        auth.signinRedirect();
      }
    } catch (error) {
      console.error('Silent refresh failed:', error);
      AuthUtil.clearStorage();
      auth.signinRedirect();
    } finally {
      setIsRefreshing(false);
    }
  }, [auth, setUserOrganizationId, isRefreshing, lastRefreshAttempt]);

  // Effect to handle token expiration
  useEffect(() => {
    if (AuthUtil.isTokenExpired() && !isRefreshing) {
      handleTokenRefresh();
    }
  }, [handleTokenRefresh, isRefreshing]);

  // Add event handlers for token expiration and refresh
  useEffect(() => {
    const handleAccessTokenExpiring = () => {
      handleTokenRefresh();
    };

    const expiringCleanup = auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    const expiredCleanup = auth.events.addAccessTokenExpired(handleAccessTokenExpiring);
    const refreshFailedCleanup = auth.events.addSilentRenewError(handleNotAuthenticated);

    return () => {
      expiringCleanup();
      expiredCleanup();
      refreshFailedCleanup();
    };
  }, [auth.events, handleTokenRefresh]);

  const hasLoggedNotAuthenticated = useRef(false);

  const validPortalUser = () => {
    let isValid = true;

    // Check if user is authenticated
    if (!auth.isAuthenticated || !auth.user) {
      // Only log warning once per unauthenticated session to avoid infinite console spam
      if (!auth.isLoading && !auth.activeNavigator) {
        if (!hasLoggedNotAuthenticated.current) {
          hasLoggedNotAuthenticated.current = true;
          console.warn('invalid - not authenticated!');
        }
      }
      isValid = false;
    } else {
      hasLoggedNotAuthenticated.current = false;
    }

    // Check permissions
    if (appPermissions === null) isValid = false;
    if (orgPermissions === null && !isRoot) isValid = false;

    return isValid;
  };

  const hasAttemptedSigninRedirect = useRef(false);

  const handleNotAuthenticated = () => {
    if (AuthUtil.isSignOutInProgress()) return;
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
      if (!hasAttemptedSigninRedirect.current) {
        hasAttemptedSigninRedirect.current = true;
        auth.signinRedirect();
      }
    } else {
      hasAttemptedSigninRedirect.current = false;
    }
  };

  // automatically sign-in (ref guards so we only call signinRedirect once per unauthenticated session, preventing max update depth from effect re-runs)
  React.useEffect(() => {
    handleNotAuthenticated();
  }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading]);

  useEffect(() => {
    if (!currentProfile) return;

    // Initialize Datadog RUM once per app load (window guard survives Portal remounts so we don't double-init when currentProfile reference changes).
    const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
    const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
    const win = window as Window & { __datadogRumInitialized?: boolean };
    if (applicationId && clientToken && !win.__datadogRumInitialized) {
      win.__datadogRumInitialized = true;
      datadogRum.init({
        applicationId,
        clientToken,
        site: process.env.REACT_APP_DATADOG_SITE || 'datadoghq.com',
        service: process.env.REACT_APP_DATADOG_SERVICE || 'phoenix-web-portal',
        env: process.env.REACT_APP_DATADOG_ENV || getEnv(),
        version: process.env.REACT_APP_DATADOG_VERSION || '1.0.0',
        sessionSampleRate: Number(process.env.REACT_APP_DATADOG_SESSION_SAMPLE_RATE) || 100,
        sessionReplaySampleRate: Number(process.env.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
      });
      datadogRum.startSessionReplayRecording();
    }

    // This function creates visitors and accounts in Pendo
    pendo.initialize({
      visitor: {
        id: [getEnv(), currentProfile.user?.id].filter((x) => x !== 'prod').join('_'),
        env: getEnv(),
        creationDate: currentProfile.user?.createdAt,
        experience: 'admin',
        email: currentProfile.user?.email,
        firstName: currentProfile.user?.firstName,
        lastName: currentProfile.user?.lastName,
      },
      account: {
        id: [getEnv(), currentProfile.organization?.id].filter((x) => x !== 'prod').join('_'),
        name: currentProfile.organization?.name,
      },
    });
  }, [currentProfile]);

  return validPortalUser() ? (
    <Routes>
      <Route key='switch_organizations' path='/switch-org/:id?' element={<SwitchOrganization />} />
      <Route key='change_password' path='/change-password/*' element={<ChangePassword />} />
      <Route key='error' path='/error/*' element={<Error />} />
      <Route id='dashboard' path='/dashboard/*' element={<Dashboard />} />
      <Route id='notifications' path='/notifications/:id?' element={<Notifications />} />
      <Route id='notifications_other' path='/notifications/*' element={<Notifications />} />
      <Route id='lexid_root' path='/lexid/*' element={<LexID />} />
      <Route id='policy_root' path='/policy/*' element={<Policy />} />
      <Route id='awb_root' path='/awb/*' element={<AWB />} />
      <Route id='accreditation_root' path='/accreditation/*' element={<Accreditation />} />
      <Route id='reporting_root' path='/reporting/*' element={<Reporting />} />
      <Route id='training_root' path='/training/*' element={<Training />} />
      <Route id='performance_reporting_root' path='/performance-reporting/*' element={<PerformanceReporting />} />
      <Route id='lex_ai_root' path='/lex-ai/*' element={<LexAI />} />
      <Route id='mfa_settings' path='/mfa-settings' element={<MFASettings />} />
      <Route id='search_manual' path='search-manuals' element={<IssuedManualsSearchPage />} />
      <Route path='dtbs/*' element={<DTBRoutes />} />
      <Route path='features' element={<Features />} />
      <Route path='kb/*' element={<KB />} />
      <Route path='*' element={<Navigate to='/admin/dashboard' replace />} />
    </Routes>
  ) : (
    <Box
      sx={{
        display: 'flex',
        minHeight: '40vh',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress />
    </Box>
  );
};
export default Portal;
